import { StateCreator } from 'zustand';

import { AuthState, CommonStoreType, UserState } from '../../types';
import {
  changeUserWallet as actionChangeUserWallet,
  deleteUserWallet as actionDeleteUserWallet,
  fetchUser as actionFetchUser,
  formatAmount as actionFormatAmount,
  insertUser as actionInsertUser,
  refreshUser as actionRefreshUser,
  resetUser as actionResetUser,
  setEmailConfirmationPopupSeen as actionSetEmailConfirmationPopupSeen,
  unFormatAmount as actionUnFormatAmount,
} from './actions';
import { REASON_FOR_UPDATING_USER } from './helpers';
import {
  compPoints as fetchCompPoints,
  compPointsRatesMoney as fetchCompPointsRatesMoney,
  User,
  Wallet,
} from '@/api/auth';
import { getCurrentCurrencyData } from '@/helpers/common';
import {
  currencyFlags,
  isCryptoCurrency,
  mapCurrencyName,
} from '@/helpers/currency';

export const initialState: UserState = {
  user: undefined,
  reasonForUpdatingUser: undefined,
  isAuthenticated: false,
  pendingUser: true,
  pendingUserPure: true,
  wallets: [],
  pendingWallets: false,
  afterLoginRedirectUrl: null,
  afterDepositRedirectUrl: null,
  compPoints: undefined,
  compointsRatesMoney: undefined,
  pendingCompointsRatesMoney: false,
  compPointsPending: false,
};

export const useAuthSlice: StateCreator<CommonStoreType, [], [], AuthState> = (
  set,
  getState
) => {
  return {
    ...initialState,
    extendedUserWallets: [],
    currentWallet: null,
    isEmailConfirmationPopup: false,
    emailConfirmationPopupHeight: 0,
    limits: [],

    fetchUser: async (reasonForUpdatingUser: REASON_FOR_UPDATING_USER) => {
      const { countryCode } = getState();
      return actionFetchUser(reasonForUpdatingUser, countryCode);
    },
    refreshUser: async () => {
      const { user, countryCode } = getState();
      return actionRefreshUser(user, countryCode);
    },
    insertUser: async (
      user: User,
      reasonForUpdatingUser: REASON_FOR_UPDATING_USER
    ) => {
      return actionInsertUser(user, reasonForUpdatingUser);
    },
    updateUserReason: (reason: REASON_FOR_UPDATING_USER) => {
      set({ reasonForUpdatingUser: reason });
    },
    resetUser: async () => actionResetUser(),

    setUserWallets: async (wallets: Wallet[]) => {
      const { currencies, user } = getState();

      const extendedUserWallets = wallets.map((wallet) => ({
        ...wallet,
        amountCents: getCurrentCurrencyData(
          wallet.amountCents,
          wallet.currency,
          currencies
        ).amount,
        subCurrency: getCurrentCurrencyData(
          wallet.amountCents,
          wallet.currency,
          currencies
        ).currency,
        name: mapCurrencyName[wallet.currency],
        isCrypto: isCryptoCurrency(currencies, wallet.currency),
        icon: currencyFlags[wallet.currency],
      }));

      const currentWallet =
        extendedUserWallets.find(
          ({ currency }) => currency === user?.currency
        ) || extendedUserWallets[0];

      set({ wallets, extendedUserWallets, currentWallet });
    },
    changeUserWallet: async (currency: Wallet['currency']) => {
      const { countryCode } = getState();
      return actionChangeUserWallet(currency, countryCode);
    },
    deleteUserWallet: async (
      prevCurrency: Wallet['currency'],
      nextCurrency?: Wallet['currency']
    ) => {
      const { countryCode } = getState();

      return actionDeleteUserWallet(prevCurrency, nextCurrency, countryCode);
    },

    formatAmount: (amount: number, withSymbol?: boolean) => {
      const { currencies, user } = getState();
      return actionFormatAmount(currencies, amount, user?.currency, withSymbol);
    },
    unFormatAmount: (amount: number) => {
      const { currencies, user } = getState();

      return actionUnFormatAmount(currencies, amount, user?.currency);
    },
    setEmailConfirmationPopupSeen: () => actionSetEmailConfirmationPopupSeen(),
    setEmailConfirmationPopupHeight: (height: number) => {
      set({ emailConfirmationPopupHeight: height });
    },
    getCompointsRatesMoney: async () => {
      try {
        set({ pendingCompointsRatesMoney: true });

        const { data: compointsRatesMoney } = await fetchCompPointsRatesMoney();

        set({ compointsRatesMoney });
      } catch (e) {
        console.log(e);
      } finally {
        set({ pendingCompointsRatesMoney: false });
      }
    },
    fetchCompoints: async () => {
      try {
        set({ compPointsPending: true });

        const { data: compPoints } = await fetchCompPoints();

        set({ compPoints });

        return;
      } catch (e) {
        console.log(e);
      } finally {
        set({ compPointsPending: false });
      }
    },
  };
};
